import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { useIdleTimer } from 'react-idle-timer';
import Auth from '@ublend-npm/aula-auth-client';
import PropTypes from 'prop-types';
import { useFlags } from 'launchdarkly-react-client-sdk';

import actions from '$data/core/core.actions';
import leaveSSA from '$utils/leaveSSA';
import { createCookie, getCookie } from '$utils/cookie';
import { selectors } from '$data/core';
import ActiveDialogBox from './ActiveDailogBox';

const AUTO_HIDE_DURATION = 30 * 1000;
const IDLE_SESSION_DURATION = 30 * 60 * 1000;
const REGENERATE_SESSION_DURATION = 25 * 60 * 1000;
const DEBOUNCE_TIME = 500;

const mapStateToProps = (state) => ({
  email: selectors.getUserEmail(state),
  institution: selectors.getInstitution(state),
});

const mapDispatchToProps = {
  regenerateSessionTokenAction: actions.regenerateSessionTokenAction,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

const SessionManager = ({
  email,
  institution,
  regenerateSessionTokenAction,
}) => {
  const LATEST_ACTIVITY_COOKIE = `${institution}-latest-activity-timestamp`;
  const SESSION_STATUS_COOKIE = `${institution}-session-status`;
  const setSessionStatusCookie = (value) => {
    createCookie(
      SESSION_STATUS_COOKIE,
      JSON.stringify(value),
      null,
      '/',
      'aula.education'
    );
  };

  const [isIdle, setIsIdle] = useState(false);
  const { ftSessionTokenRefreshEnabled202412 } = useFlags();

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (ftSessionTokenRefreshEnabled202412) {
      setSessionStatusCookie({ sessionStatus: true, loggedOut: false });
      createCookie(
        LATEST_ACTIVITY_COOKIE,
        new Date().getTime(),
        null,
        '/',
        'aula.education'
      );
      const regenerateToken = () => {
        if (!isIdle) {
          regenerateSessionTokenAction();
        }
      };

      // Set up interval for token regeneration
      const interval = setInterval(
        regenerateToken,
        REGENERATE_SESSION_DURATION
      );

      // Cleanup function
      return () => clearInterval(interval);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ftSessionTokenRefreshEnabled202412, regenerateSessionTokenAction]);

  const handleLogout = async () => {
    await Auth.requestLogout(email, institution)
      .catch(() => null)
      .finally(() => {
        setIsIdle(false);
        setSessionStatusCookie({ sessionStatus: false, loggedOut: true });
        leaveSSA({ returnAfterLogin: true });
      });
  };

  const handleOnIdle = () => {
    let value = false;
    const sessionStatus = JSON.parse(
      unescape(getCookie(SESSION_STATUS_COOKIE))
    );
    // if session has expired on any other tab
    if (!sessionStatus.sessionStatus) {
      setIsIdle(true);
      if (sessionStatus.loggedOut) handleLogout();
      value = true;
    } else {
      const isValidSession =
        new Date().getTime() - Number(getCookie(LATEST_ACTIVITY_COOKIE)) <
        IDLE_SESSION_DURATION;
      if (!isValidSession) {
        setIsIdle(true);
        setSessionStatusCookie({ sessionStatus: false, loggedOut: false });
        value = true;
      } else {
        setIsIdle(false);
      }
    }
    return value;
  };

  useIdleTimer({
    onAction: () =>
      createCookie(
        LATEST_ACTIVITY_COOKIE,
        new Date().getTime(),
        null,
        '/',
        'aula.education'
      ),
    debounce: DEBOUNCE_TIME,
  });

  useEffect(() => {
    const interval = setInterval(async () => {
      if (ftSessionTokenRefreshEnabled202412) {
        await handleOnIdle();
      } else {
        clearInterval(interval);
      }
    }, AUTO_HIDE_DURATION);
    return () => {
      clearInterval(interval);
    };
  }, [ftSessionTokenRefreshEnabled202412]);

  useEffect(() => {
    const HEARTBEAT_INTERVAL = 5000;
    const SLEEP_DRIFT_THRESHOLD = 2 * HEARTBEAT_INTERVAL;

    let lastTick = Date.now();

    const runDriftCheck = async (runAnyhow = false) => {
      const now = Date.now();
      const drift = now - lastTick;
      lastTick = now;

      if (drift > SLEEP_DRIFT_THRESHOLD || runAnyhow) {
        const idleStatus = handleOnIdle();
        if (idleStatus === false) {
          try {
            await regenerateSessionTokenAction();
          } catch (error) {
            setIsIdle(true);
            setSessionStatusCookie({ sessionStatus: false, loggedOut: false });
          }
        }
      }
    };

    const runDriftWithTrue = () => {
      runDriftCheck(true);
    };

    if (ftSessionTokenRefreshEnabled202412) {
      const interval = setInterval(runDriftCheck, HEARTBEAT_INTERVAL);
      document.addEventListener('visibilitychange', runDriftCheck);
      window.addEventListener('online', runDriftWithTrue);

      return () => {
        document.removeEventListener('visibilitychange', runDriftCheck);
        window.removeEventListener('online', runDriftWithTrue);
        clearInterval(interval);
      };
    }

    return () => {};
  }, [ftSessionTokenRefreshEnabled202412]);

  const handleRegenerateSessionToken = async () => {
    await regenerateSessionTokenAction();
    setIsIdle(false);
    setSessionStatusCookie({ sessionStatus: true, loggedOut: false });
  };

  return (
    <>
      {ftSessionTokenRefreshEnabled202412 && (
        <ActiveDialogBox
          open={isIdle}
          handleRegenerateSessionToken={handleRegenerateSessionToken}
          handleSessionLogout={handleLogout}
        />
      )}
    </>
  );
};

SessionManager.propTypes = {
  regenerateSessionTokenAction: PropTypes.func.isRequired,
  email: PropTypes.string.isRequired,
  institution: PropTypes.string.isRequired,
};

export default connector(SessionManager);
